import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/products";

export default function CategoriesSection() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-[#f5ede0]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)" }}
        >
          <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
            — Our Collections —
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cat_title")}
          </h2>
          <p className="text-gray-600 text-lg max-w-xl mx-auto">{t("cat_subtitle")}</p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/shop?category=${cat.id}`}
              className="group relative overflow-hidden rounded-2xl cursor-pointer block"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
                transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)`,
                transitionDelay: `${i * 0.08}s`,
              }}
            >
              {/* Image */}
              <div className="relative h-52 md:h-64 overflow-hidden">
                <img
                  src={cat.image}
                  alt={isFr ? cat.labelF : cat.label}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-rose-900/80 via-rose-900/20 to-transparent transition-opacity duration-300 group-hover:opacity-90" />

                {/* Icon */}
                <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white transition-all duration-300 group-hover:bg-rose-500 group-hover:scale-110">
                  <i className={`${cat.icon} text-base`}></i>
                </div>

                {/* Label */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3
                    className="text-white font-bold text-base md:text-lg leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {isFr ? cat.labelF : cat.label}
                  </h3>
                  <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <span className="text-amber-200 text-xs font-medium">{t("cat_explore")}</span>
                    <i className="ri-arrow-right-line text-amber-200 text-xs"></i>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}